import React, { useEffect, useState } from 'react';
import { MailCheck, Newspaper } from 'lucide-react';
import { api } from '../hooks/useApi';
import { newsdeskInfo, isSent, canMarkSent, defaultSentDay } from '../lib/newsdeskSent';

// Small "Sent" pill for table rows.
export const SentPill = ({ booking }) => {
  if (!isSent(booking)) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
      title="Newsdesk confirmed this newsletter ad was sent"
      data-testid={`booking-sent-pill-${booking.id}`}
    >
      <MailCheck className="w-3 h-3" /> Sent
    </span>
  );
};

// Details box: when it was sent, in which newsletter, and whether
// Newsdesk created the booking. Renders nothing for ordinary bookings.
const NewsdeskSentInfo = ({ booking }) => {
  const info = newsdeskInfo(booking);
  if (!info) return null;
  return (
    <div
      className={`p-3 rounded-md border text-xs space-y-1 ${
        info.sent
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-[#0A0A0A] border-[#1E293B]'
      }`}
      data-testid="booking-newsdesk-info"
    >
      {info.sent && (
        <p className="flex items-center gap-1.5 text-emerald-400 font-medium">
          <MailCheck className="w-3.5 h-3.5" /> Sent {info.sentDay}
        </p>
      )}
      {info.subject && (
        <p className="text-[#94A3B8]">
          Newsletter: <span className="text-[#F8FAFC]">{info.subject}</span>
        </p>
      )}
      {info.movedFrom && (
        <p className="text-[#94A3B8]">Originally scheduled for {info.movedFrom}</p>
      )}
      {info.manual && (
        <p className="text-[#94A3B8]">
          Marked by hand{info.markedBy ? ` by ${info.markedBy}` : ''}
        </p>
      )}
      {info.byNewsdesk && (
        <p className="flex items-center gap-1.5 text-[#94A3B8]">
          <Newspaper className="w-3.5 h-3.5" />
          {info.sent
            ? 'Booking created by Newsdesk — review before invoicing'
            : 'Created by Newsdesk from a newsletter draft — not sent yet'}
        </p>
      )}
    </div>
  );
};

// "Mark as sent" / "Remove sent mark" for a digital booking. Renders
// nothing for print bookings, cancelled ones, or read-only users.
// `onChanged` runs after a successful change (reload the list).
export const SentControl = ({ booking, canEdit = true, onChanged }) => {
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDay(defaultSentDay(booking));
    setError('');
  }, [booking]);

  if (!canEdit || !canMarkSent(booking)) return null;
  const sent = isSent(booking);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update the sent mark.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1" data-testid="booking-sent-control">
      {sent ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => api.delete(`/digital/bookings/${booking.id}/sent`))}
          className="text-xs text-[#94A3B8] underline hover:text-[#F8FAFC] disabled:opacity-50"
          data-testid="booking-unmark-sent-btn"
        >
          {busy ? 'Removing…' : 'Remove sent mark'}
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            disabled={busy}
            className="bg-[#0A0A0A] border border-[#1E293B] text-[#F8FAFC] rounded-md px-2 h-8 text-xs"
            data-testid="booking-sent-date"
          />
          <button
            type="button"
            disabled={busy || !day}
            onClick={() => run(() => api.put(`/digital/bookings/${booking.id}/sent`, { sent_date: day }))}
            className="inline-flex items-center gap-1 px-2 h-8 rounded-md text-xs border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
            data-testid="booking-mark-sent-btn"
          >
            <MailCheck className="w-3.5 h-3.5" /> {busy ? 'Saving…' : 'Mark as sent'}
          </button>
        </div>
      )}
      {error && <p className="text-red-400 text-xs">{error}</p>}
    </div>
  );
};

export default NewsdeskSentInfo;
